import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import SelectSearch from 'react-select-search';
import Fuse from 'fuse.js';
import Loader from 'react-loader-spinner';
import { FiAward, FiSave, FiX } from 'react-icons/fi';
import axios from 'axios';
import { BASE_URL } from '../constants/config';
import { getTokensFromStore, checkIfTokenExpired } from '../utilities/tokens';
import { refreshTokens } from '../actions/usersActions';
import '../styles/leagueChampionsManagement.css';

const DEFAULT_PLACES = [1, 2, 3].map((place) => ({
  place,
  data: []
}));

function LeagueChampionsManagement() {
  const dispatch = useDispatch();

  const [championships, setChampionships] = useState([]);
  const [selectedChampionship, setSelectedChampionship] = useState(null);
  const [leagues, setLeagues] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [teams, setTeams] = useState([]);
  const [leagueChamps, setLeagueChamps] = useState(DEFAULT_PLACES);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    loadChampionships();
  }, []);

  useEffect(() => {
    if (selectedLeague && selectedSeason) {
      loadLeagueData();
    } else {
      setTeams([]);
      setLeagueChamps(DEFAULT_PLACES);
    }
  }, [selectedLeague, selectedSeason]);

  const findByValue = (items, value) => {
    return items.find((item) => String(item.value) === String(value));
  };

  const fuzzySearch = (options) => {
    const fuse = new Fuse(options, {
      keys: ['name'],
      threshold: 0.3,
    });

    return (value) => {
      if (!value.length) {
        return options;
      }
      return fuse.search(value).map((item) => item.item);
    };
  };

  const getAuthHeaders = async () => {
    const isTokenExpired = checkIfTokenExpired();
    if (isTokenExpired) {
      await dispatch(refreshTokens());
    }

    const tokens = getTokensFromStore();
    return {
      Authorization: 'Bearer ' + tokens.access_token,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };
  };

  const extractList = (payload, keys = []) => {
    if (Array.isArray(payload)) {
      return payload;
    }

    if (!payload || typeof payload !== 'object') {
      return [];
    }

    for (let index = 0; index < keys.length; index += 1) {
      const key = keys[index];
      if (Array.isArray(payload[key])) {
        return payload[key];
      }
      if (payload[key] && Array.isArray(payload[key].data)) {
        return payload[key].data;
      }
    }

    if (Array.isArray(payload.data)) {
      return payload.data;
    }

    return [];
  };

  const normalizeChampionPlaces = (championsData) => {
    const normalized = DEFAULT_PLACES.map((item) => ({ ...item }));

    championsData.forEach((item) => {
      const place = Number(item.place || item.position || item.rank || item.id);
      if (![1, 2, 3].includes(place)) {
        return;
      }

      const nestedTeam = item.data && item.data.length > 0 ? item.data[0] : null;
      const teamId = item.team_id || item.teamId || nestedTeam?.team_id || nestedTeam?.id || false;
      const teamName = item.team_name || item.teamName || nestedTeam?.team_name || nestedTeam?.name || '';

      normalized[place - 1] = {
        ...item,
        place,
        team_id: teamId,
        team_name: teamName,
        data: teamId ? [{ ...(nestedTeam || {}), team_id: teamId, name: teamName || nestedTeam?.name || '' }] : []
      };
    });

    return normalized;
  };

  const loadChampionships = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(
        BASE_URL.url + '/leagues/1/adminLeagues',
        {},
        { headers }
      );

      const leaguesData = extractList(response.data);

      if (leaguesData.length > 0) {
        const championshipsMap = {};

        leaguesData.forEach((item) => {
          const championshipName = item.championat_name || 'Без чемпионата';

          if (!championshipsMap[championshipName]) {
            championshipsMap[championshipName] = {
              value: championshipName,
              name: championshipName,
              leagues: []
            };
          }

          if (item.league_id > 0) {
            championshipsMap[championshipName].leagues.push({
              value: item.league_id,
              name: item.league_name,
              seasons: item.seasons || [],
              ...item
            });
          }
        });

        setChampionships(
          Object.values(championshipsMap)
            .filter((item) => item.leagues.length > 0)
            .map((item, index) => ({
              ...item,
              key: index,
            }))
        );
      } else {
        setChampionships([]);
      }
    } catch (error) {
      console.error('Error loading championships:', error);
      setChampionships([]);
      setMessage({ text: 'Не удалось загрузить список лиг', type: 'error' });
    }
  };

  const loadLeagueData = async () => {
    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      await Promise.all([loadLeagueTeams(), loadLeagueChampions()]);
    } catch (error) {
      setMessage({ text: 'Не удалось загрузить данные по призерам лиги', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadLeagueTeams = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(
        BASE_URL.url + `/leagues/${selectedLeague.value}/teams`,
        {
          league_id: selectedLeague.value,
          season_id: selectedSeason.value,
        },
        { headers }
      );

      const teamsData = extractList(response.data, ['teams']);

      setTeams(
        teamsData.map((item, index) => ({
          ...item,
          value: item.team_id || item.id,
          name: item.name || item.team_name,
          key: index,
        }))
      );
    } catch (error) {
      console.error('Error loading league teams:', error);
      setTeams([]);
      throw error;
    }
  };

  const loadLeagueChampions = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(
        BASE_URL.url + `/leagues/${selectedLeague.value}/getLeagueChampions`,
        {
          league_id: selectedLeague.value,
          season_id: selectedSeason.value,
        },
        { headers }
      );

      const championsData = extractList(response.data, ['champions', 'trophies']);
      setLeagueChamps(normalizeChampionPlaces(championsData));
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setLeagueChamps(DEFAULT_PLACES);
        return;
      }

      console.error('Error loading league champions:', error);
      setLeagueChamps(DEFAULT_PLACES);
      throw error;
    }
  };

  const handleChampionshipChange = (championshipId) => {
    const championship = findByValue(championships, championshipId);
    setSelectedChampionship(championship || null);
    setSelectedLeague(null);
    setSelectedSeason(null);
    setTeams([]);
    setLeagueChamps(DEFAULT_PLACES);
    setMessage({ text: '', type: '' });

    if (championship && championship.leagues && championship.leagues.length > 0) {
      setLeagues(championship.leagues);
    } else {
      setLeagues([]);
    }

    setSeasons([]);
  };

  const handleLeagueChange = (leagueId) => {
    const league = findByValue(leagues, leagueId);
    setSelectedLeague(league || null);
    setSelectedSeason(null);
    setTeams([]);
    setLeagueChamps(DEFAULT_PLACES);
    setMessage({ text: '', type: '' });

    if (league && league.seasons && league.seasons.length > 0) {
      setSeasons(
        league.seasons.map((item, index) => ({
          value: item.season_id,
          name: item.season_name,
          key: index,
          ...item,
        }))
      );
    } else {
      setSeasons([]);
    }
  };

  const handleSeasonChange = (seasonId) => {
    const season = findByValue(seasons, seasonId);
    setSelectedSeason(season || null);
    setTeams([]);
    setLeagueChamps(DEFAULT_PLACES);
    setMessage({ text: '', type: '' });
  };

  const getAssignedTeamId = (item) => {
    if (item.data && item.data.length > 0 && item.data[0].team_id) {
      return item.data[0].team_id;
    }

    return item.team_id || false;
  };

  const findDuplicatePlace = (teamId, currentPlace) => {
    if (!teamId) {
      return null;
    }

    return leagueChamps.find((item) => {
      return item.place !== currentPlace && String(getAssignedTeamId(item)) === String(teamId);
    });
  };

  const getDuplicateTeamIds = () => {
    const teamCounts = leagueChamps.reduce((accumulator, item) => {
      const teamId = getAssignedTeamId(item);

      if (!teamId) {
        return accumulator;
      }

      const normalizedTeamId = String(teamId);
      accumulator[normalizedTeamId] = (accumulator[normalizedTeamId] || 0) + 1;
      return accumulator;
    }, {});

    return Object.keys(teamCounts).filter((teamId) => teamCounts[teamId] > 1);
  };

  const handleChampionChange = (place, teamId) => {
    const selectedTeam = teamId ? findByValue(teams, teamId) : null;

    if (selectedTeam) {
      const duplicatePlace = findDuplicatePlace(selectedTeam.value, place);

      if (duplicatePlace) {
        setMessage({
          text: `Команда "${selectedTeam.name}" уже выбрана для ${duplicatePlace.place} места`,
          type: 'error'
        });
        return;
      }
    }

    setMessage({ text: '', type: '' });

    setLeagueChamps((prevState) => prevState.map((item) => {
      if (item.place !== place) {
        return item;
      }

      return {
        ...item,
        team_id: selectedTeam ? selectedTeam.value : false,
        team_name: selectedTeam ? selectedTeam.name : '',
        data: selectedTeam
          ? [{ team_id: selectedTeam.value, name: selectedTeam.name }]
          : []
      };
    }));
  };

  const clearChampion = (place) => {
    handleChampionChange(place, '');
  };

  const getChampionTeamId = (item) => {
    return getAssignedTeamId(item) || '';
  };

  const getChampionTeamName = (item) => {
    const selectedTeamId = getChampionTeamId(item);
    const selectedTeam = selectedTeamId ? findByValue(teams, selectedTeamId) : null;
    return selectedTeam?.name || item.team_name || 'Команда не выбрана';
  };

  const handleSave = async () => {
    if (!selectedLeague || !selectedSeason) {
      setMessage({ text: 'Выберите лигу и сезон', type: 'error' });
      return;
    }

    if (getDuplicateTeamIds().length > 0) {
      setMessage({ text: 'Нельзя назначить одну и ту же команду на несколько призовых мест', type: 'error' });
      return;
    }

    setSaving(true);
    setMessage({ text: '', type: '' });

    try {
      const headers = await getAuthHeaders();
      const trophies = leagueChamps.map((it) => ({
        place: it.place,
        team_id: it.data && it.data.length && it.data[0] && it.data[0].team_id ? it.data[0].team_id : false
      }));

      await axios.post(
        BASE_URL.url + `/leagues/${selectedLeague.value}/add_trophies`,
        {
          league_id: selectedLeague.value,
          season_id: selectedSeason.value,
          trophies,
        },
        { headers }
      );

      setMessage({ text: 'Призеры успешно сохранены', type: 'success' });
      await loadLeagueChampions();
    } catch (error) {
      console.error('Error saving league champions:', error);
      setMessage({ text: 'Не удалось сохранить призеров', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const teamOptions = [{ value: '', name: 'Не выбрано', key: 'empty-team-option' }, ...teams];

  return (
    <div className="layoutWrapper league-champions-page">
      <div className="matchCenterWrapper">
        <div className="matchCenterHeader">
          <div className="league-champions-header">
            <div>
              <span className="matchCenterName">Призеры лиги</span>
              <p>Настройка 1, 2 и 3 места для выбранной лиги и сезона.</p>
            </div>

            {selectedLeague && selectedSeason && (
              <button
                className="league-champions-save-btn"
                onClick={handleSave}
                disabled={saving || loading}
              >
                <FiSave />
                {saving ? 'Сохранение...' : 'Сохранить'}
              </button>
            )}
          </div>

          <div className="league-champions-filters">
            <div className="league-champions-filter-group">
              <label>Чемпионат</label>
              <SelectSearch
                options={championships}
                search
                value={selectedChampionship?.value}
                onChange={handleChampionshipChange}
                filterOptions={fuzzySearch}
                emptyMessage="Чемпионат не найден"
                placeholder="Выберите чемпионат"
              />
            </div>

            {selectedChampionship && leagues.length > 0 && (
              <div className="league-champions-filter-group">
                <label>Лига</label>
                <SelectSearch
                  options={leagues}
                  search
                  value={selectedLeague?.value}
                  onChange={handleLeagueChange}
                  filterOptions={fuzzySearch}
                  emptyMessage="Лига не найдена"
                  placeholder="Выберите лигу"
                />
              </div>
            )}

            {selectedLeague && seasons.length > 0 && (
              <div className="league-champions-filter-group">
                <label>Сезон</label>
                <SelectSearch
                  options={seasons}
                  search
                  value={selectedSeason?.value}
                  onChange={handleSeasonChange}
                  filterOptions={fuzzySearch}
                  emptyMessage="Сезон не найден"
                  placeholder="Выберите сезон"
                />
              </div>
            )}
          </div>
        </div>

        <div className="league-champions-container">
          {message.text && (
            <div className={`league-champions-message ${message.type === 'success' ? 'success' : 'error'}`}>
              {message.text}
            </div>
          )}

          {loading ? (
            <div className="league-champions-loading">
              <Loader type="Rings" color="#d69e2e" height={50} width={50} />
              <span>Загрузка призеров и команд...</span>
            </div>
          ) : selectedLeague && selectedSeason ? (
            <div className="league-champions-grid">
              {leagueChamps.map((item) => (
                <div key={item.place} className={`league-champion-card place-${item.place}`}>
                  <div className="league-champion-card-header">
                    <div className="league-champion-place-badge">
                      <FiAward />
                      <span>{item.place} место</span>
                    </div>
                    <button
                      type="button"
                      className="league-champion-clear-btn"
                      onClick={() => clearChampion(item.place)}
                      disabled={saving || loading}
                    >
                      <FiX />
                    </button>
                  </div>

                  <div className="league-champion-card-body">
                    <div className="league-champion-current-team">
                      {getChampionTeamName(item)}
                    </div>

                    <div className="league-champion-select-wrapper">
                      <label>Команда</label>
                      <SelectSearch
                        options={teamOptions}
                        search
                        value={getChampionTeamId(item)}
                        onChange={(teamId) => handleChampionChange(item.place, teamId)}
                        filterOptions={fuzzySearch}
                        emptyMessage="Команда не найдена"
                        placeholder="Выберите команду"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="league-champions-empty-state">
              <h3>Выберите чемпионат, лигу и сезон</h3>
              <p>После выбора загрузятся текущие призеры и список команд для настройки 1-3 мест.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default LeagueChampionsManagement;